/**
 * Tencent Cloud SOE new version WSS evaluation service.
 * Docs: https://cloud.tencent.com/document/product/1774/107497
 *
 * Keeps the old TencentSOE-like start/stop shape used by the vocabulary pages.
 */

import HmacSha1 from 'crypto-js/hmac-sha1';
import Base64 from 'crypto-js/enc-base64';
import Vocabulary from '@/servers/modules/vocabulary/vocabulary';

type Primitive = string | number;

interface StartOptions {
  RefText: string;
  error?: (e: any) => void;
  complete?: () => void;
  success?: (r: any) => void;
}

interface StopOptions {
  success?: (res: any) => void;
  error?: (e: any) => void;
}

function uuidv4(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function getFirstValue(source: any, keys: string[]): any {
  for (let i = 0; i < keys.length; i += 1) {
    if (source && source[keys[i]] !== undefined && source[keys[i]] !== null) {
      return source[keys[i]];
    }
  }
  return '';
}

function getNumberField(source: any, keys: string[]): number {
  const value = getFirstValue(source, keys);
  const parsed = Number(value);
  return Number.isNaN(parsed) ? 0 : parsed;
}

function mask(value: string): string {
  if (!value) return '';
  if (value.length <= 8) return '****';
  return `${value.slice(0, 4)}****${value.slice(-4)}`;
}

class VoiceEvaluationService {
  private config: any = {};

  private credentials: any = null;

  private ws: WebSocket | null = null;

  private audioContext: AudioContext | null = null;

  private mediaStream: MediaStream | null = null;

  private processor: ScriptProcessorNode | null = null;

  private mediaStreamSource: MediaStreamAudioSourceNode | null = null;

  private recordedChunks: Blob[] = [];

  private mediaRecorder: any = null;

  private pcmBuffer: Int16Array = new Int16Array(0);

  private lastResult: any = null;

  private stopOptions: StopOptions | null = null;

  private stopBlob: Blob | null = null;

  private resultResolved = false;

  private fallbackTimer: number | null = null;

  private isStarting = false;

  private queuedStopOptions: StopOptions | null = null;

  isRecording = false;

  score = 0;

  evalMode = 0;

  errCode: string | number = '';

  constructor(config: any = {}) {
    this.config = config || {};
    if (config && config.EvalMode !== undefined) {
      this.evalMode = config.EvalMode;
    }
  }

  private normalizeCredentials(raw: any): any {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (!parsed) return {};
    if (!parsed.Credentials && !parsed.credentials && (parsed.TmpSecretId || parsed.SecretId)) {
      return {
        ...parsed,
        Credentials: parsed,
      };
    }
    return parsed;
  }

  async getCredentials(): Promise<any> {
    if (this.config && typeof this.config.getAuthorization === 'function') {
      this.credentials = await new Promise((resolve, reject) => {
        try {
          this.config.getAuthorization((authorization: any) => {
            resolve(this.normalizeCredentials(authorization));
          });
        } catch (e) {
          reject(e);
        }
      });
      return this.credentials;
    }

    const res = await Vocabulary.getTTSToken(null);
    if (res.data && res.data.code === 0) {
      this.credentials = this.normalizeCredentials(res.data.data);
      return this.credentials;
    }
    throw new Error('Failed to get SOE temporary credentials');
  }

  async ensureCredentials(): Promise<void> {
    if (!this.credentials || !this.credentials.ExpiredTime) {
      await this.getCredentials();
      return;
    }
    const expireMs = Number(this.credentials.ExpiredTime) * 1000;
    const leftMin = (expireMs - Date.now()) / 60000;
    if (leftMin < 10) {
      await this.getCredentials();
    }
  }

  private signParams(appid: string, params: Record<string, Primitive>, secretKey: string): string {
    const qs = Object.keys(params)
      .sort()
      .map((key) => `${key}=${params[key]}`)
      .join('&');
    const signStr = `soe.cloud.tencent.com/soe/api/${appid}?${qs}`;
    return Base64.stringify(HmacSha1(signStr, secretKey));
  }

  private async buildWssUrl(refText: string): Promise<string> {
    await this.ensureCredentials();
    const c = this.credentials;
    const credentials = c.Credentials || c.credentials || {};
    const envAppid = typeof process !== 'undefined' && process.env
      ? process.env.VUE_APP_SOE_APPID
      : '';
    const windowAppid = typeof window !== 'undefined'
      ? getFirstValue(window as any, ['SOE_APPID', 'SoeAppId', 'soeAppId'])
      : '';
    const appid = '1307033263';
    const secretid = 'AKIDlX5OVO3UgdQyINUPWP5Xl5NVUFo9QUKx';
    const secretKey = 'ShiW6TcXGl25vd8QUKwPgjYAYnpdEbw3';
    const token = null;
    const now = Math.floor(Date.now() / 1000);

    if (!appid || !secretid || !secretKey) {
      console.error('[VoiceEvaluation] SOE credentials are incomplete', {
        appid,
        secretid: mask(secretid),
        hasSecretKey: !!secretKey,
        hasToken: !!token,
        credentialKeys: c ? Object.keys(c) : [],
        nestedCredentialKeys: credentials ? Object.keys(credentials) : [],
      });
      throw new Error(!appid ? 'SOE credentials are incomplete: missing AppId' : 'SOE credentials are incomplete');
    }

    const params: Record<string, Primitive> = {
      eval_mode: this.evalMode,
      expired: now + 3600,
      nonce: Math.floor(Math.random() * 1000000000),
      ref_text: refText,
      score_coeff: 1.0,
      secretid,
      sentence_info_enabled: 1,
      server_engine_type: '16k_en',
      text_mode: 0,
      timestamp: now,
      voice_format: 0,
      voice_id: uuidv4(),
    };

    if (token) {
      params.token = token;
    }

    const signature = this.signParams(appid, params, secretKey);
    const encoded = Object.keys(params)
      .sort()
      .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(String(params[key]))}`)
      .concat(`signature=${encodeURIComponent(signature)}`)
      .join('&');

    const url = `wss://soe.cloud.tencent.com/soe/api/${encodeURIComponent(appid)}?${encoded}`;
    console.info('[VoiceEvaluation] connecting SOE WSS', {
      appid,
      secretid: mask(secretid),
      evalMode: this.evalMode,
      voiceFormat: params.voice_format,
      engine: params.server_engine_type,
      refTextLength: refText ? refText.length : 0,
    });
    return url;
  }

  private connect(refText: string, onMessage: (msg: any) => void): Promise<WebSocket> {
    return this.buildWssUrl(refText).then((url) => new Promise<WebSocket>((resolve, reject) => {
      const ws = new WebSocket(url);
      let opened = false;
      let handshaked = false;
      const timer = window.setTimeout(() => {
        if (!handshaked) {
          reject(new Error(opened ? 'SOE handshake timeout' : 'SOE connection timeout'));
          ws.close();
        }
      }, 10000);

      ws.binaryType = 'arraybuffer';
      ws.onopen = () => {
        opened = true;
        console.info('[VoiceEvaluation] WSS opened, waiting handshake');
      };
      ws.onerror = (e) => {
        console.error('[VoiceEvaluation] WSS error', e);
        if (!handshaked) {
          reject(e);
        }
      };
      ws.onclose = () => {
        console.info('[VoiceEvaluation] WSS closed');
        this.ws = null;
      };
      ws.onmessage = (event: MessageEvent) => {
        const msg = this.parseMessage(event.data);
        if (!msg) return;
        console.info('[VoiceEvaluation] WSS message', msg);
        if (!handshaked) {
          if (msg.code === 0) {
            handshaked = true;
            window.clearTimeout(timer);
            resolve(ws);
            return;
          }
          window.clearTimeout(timer);
          reject(msg);
          ws.close();
          return;
        }
        onMessage(msg);
      };

      this.ws = ws;
    }));
  }

  async start(options: StartOptions): Promise<void> {
    if (this.isRecording || this.isStarting) return;
    this.resetResultState();
    this.recordedChunks = [];
    this.pcmBuffer = new Int16Array(0);
    this.isStarting = true;

    try {
      const ws = await this.connect(options.RefText, (msg) => this.handleEvaluationMessage(msg, options));
      await this.startAudioCapture(ws);
      this.isRecording = true;
      this.isStarting = false;
      if (options.success) options.success({ code: 0 });
      if (this.queuedStopOptions) {
        const queued = this.queuedStopOptions;
        this.queuedStopOptions = null;
        this.stop(queued);
      }
    } catch (e) {
      this.errCode = (e && (e as any).code) || 'START_FAIL';
      this.isStarting = false;
      this.cleanupAudio();
      console.error('[VoiceEvaluation] start failed', e);
      if (options.error) options.error(e);
      if (this.queuedStopOptions && this.queuedStopOptions.error) {
        this.queuedStopOptions.error(e);
      }
      this.queuedStopOptions = null;
    }
  }

  stop(options: StopOptions = {}): void {
    if (this.isStarting) {
      this.queuedStopOptions = options;
      return;
    }
    if (!this.isRecording) {
      if (options.error) options.error({ code: 'NOT_RECORDING' });
      return;
    }

    this.stopOptions = options;
    this.isRecording = false;
    this.cleanupAudio();
    this.stopBlob = this.recordedChunks.length > 0
      ? new Blob(this.recordedChunks, { type: 'audio/webm' })
      : null;

    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.flushPcmBuffer(this.ws);
      this.ws.send(JSON.stringify({ type: 'end' }));
      this.fallbackTimer = window.setTimeout(() => {
        this.resolveResult();
      }, 10000);
    } else {
      this.resolveResult();
    }
  }

  destroy(): void {
    this.isStarting = false;
    this.isRecording = false;
    this.cleanupAudio();
    this.clearFallbackTimer();
    if (this.ws) {
      this.ws.onclose = null;
      this.ws.close();
      this.ws = null;
    }
    this.recordedChunks = [];
    this.pcmBuffer = new Int16Array(0);
    this.stopOptions = null;
    this.queuedStopOptions = null;
  }

  private async startAudioCapture(ws: WebSocket): Promise<void> {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    this.mediaStream = stream;

    const MediaRecorderClass = (window as any).MediaRecorder;
    if (MediaRecorderClass) {
      const mime = MediaRecorderClass.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : 'audio/webm';
      this.mediaRecorder = new MediaRecorderClass(stream, { mimeType: mime });
      this.mediaRecorder.ondataavailable = (e: any) => {
        if (e.data && e.data.size > 0) this.recordedChunks.push(e.data);
      };
      this.mediaRecorder.start(100);
    }

    const AudioContextClass = (window as any).AudioContext || (window as any).webkitAudioContext;
    const ctx = new AudioContextClass();
    this.audioContext = ctx;
    const source = ctx.createMediaStreamSource(stream);
    const processor = ctx.createScriptProcessor(2048, 1, 1);
    this.mediaStreamSource = source;
    this.processor = processor;

    source.connect(processor);
    processor.connect(ctx.destination);
    processor.onaudioprocess = (e: any) => {
      if (!this.isRecording || ws.readyState !== WebSocket.OPEN) return;
      const input = e.inputBuffer.getChannelData(0);
      const pcm16k = this.floatTo16kPcm(input, ctx.sampleRate);
      this.sendPcmChunks(ws, pcm16k);
    };
  }

  private floatTo16kPcm(input: Float32Array, sourceRate: number): Int16Array {
    if (sourceRate === 16000) {
      return this.floatToPcm(input);
    }

    const ratio = sourceRate / 16000;
    const length = Math.floor(input.length / ratio);
    const output = new Float32Array(length);

    for (let i = 0; i < length; i += 1) {
      const start = Math.floor(i * ratio);
      const end = Math.min(Math.floor((i + 1) * ratio), input.length);
      let sum = 0;
      let count = 0;
      for (let j = start; j < end; j += 1) {
        sum += input[j];
        count += 1;
      }
      output[i] = count > 0 ? sum / count : input[start] || 0;
    }

    return this.floatToPcm(output);
  }

  private floatToPcm(input: Float32Array): Int16Array {
    const pcm = new Int16Array(input.length);
    for (let i = 0; i < input.length; i += 1) {
      const s = Math.max(-1, Math.min(1, input[i]));
      pcm[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
    }
    return pcm;
  }

  private sendPcmChunks(ws: WebSocket, chunk: Int16Array): void {
    const merged = new Int16Array(this.pcmBuffer.length + chunk.length);
    merged.set(this.pcmBuffer, 0);
    merged.set(chunk, this.pcmBuffer.length);

    const samplesPerPacket = 640;
    let offset = 0;
    while (merged.length - offset >= samplesPerPacket) {
      const packet = merged.slice(offset, offset + samplesPerPacket);
      ws.send(packet.buffer);
      offset += samplesPerPacket;
    }

    this.pcmBuffer = merged.slice(offset);
  }

  private flushPcmBuffer(ws: WebSocket): void {
    if (this.pcmBuffer.length === 0 || ws.readyState !== WebSocket.OPEN) return;
    ws.send(this.pcmBuffer.buffer);
    this.pcmBuffer = new Int16Array(0);
  }

  private handleEvaluationMessage(msg: any, startOptions?: StartOptions): void {
    if (msg.code !== 0) {
      this.errCode = msg.code;
      if (startOptions && startOptions.error) {
        startOptions.error({ code: msg.code, message: msg.message });
      }
      if (this.stopOptions && this.stopOptions.error) {
        this.stopOptions.error({ code: msg.code, message: msg.message });
      }
      this.resolveResult(true);
      return;
    }

    if (msg.result) {
      this.lastResult = this.normalizeResult(msg.result);
    }

    if (msg.final === 1) {
      this.resolveResult();
      if (this.ws) this.ws.close();
    }
  }

  private parseMessage(data: any): any {
    if (typeof data !== 'string') return null;
    try {
      return JSON.parse(data);
    } catch (_) {
      return null;
    }
  }

  private normalizeResult(result: any): any {
    if (typeof result === 'string') {
      return {
        SuggestedScore: this.getResultNumber(result, 'SuggestedScore'),
        PronAccuracy: this.getResultNumber(result, 'PronAccuracy'),
        PronFluency: this.getResultNumber(result, 'PronFluency'),
        PronCompletion: this.getResultNumber(result, 'PronCompletion'),
        rawResult: result,
      };
    }

    return {
      SuggestedScore: getNumberField(result, ['SuggestedScore', 'suggested_score', 'suggestedScore']),
      PronAccuracy: getNumberField(result, ['PronAccuracy', 'pron_accuracy', 'pronAccuracy']),
      PronFluency: getNumberField(result, ['PronFluency', 'pron_fluency', 'pronFluency']),
      PronCompletion: getNumberField(result, ['PronCompletion', 'pron_completion', 'pronCompletion']),
      Words: getFirstValue(result, ['Words', 'words']) || [],
      rawResult: result,
    };
  }

  private getResultNumber(result: string, key: string): number {
    const match = new RegExp(`${key}:\\s*(-?\\d+(?:\\.\\d+)?)`).exec(result);
    return match ? Number(match[1]) : 0;
  }

  private resolveResult(isError = false): void {
    if (this.resultResolved) return;
    this.resultResolved = true;
    this.clearFallbackTimer();

    const result = this.lastResult || {};
    const suggestedScore = Math.max(0, Math.round(Number(result.SuggestedScore) || 0));
    this.score = suggestedScore;

    if (isError && this.stopOptions && this.stopOptions.error) {
      this.stopOptions.error({ code: this.errCode || 'SOE_ERROR' });
    } else if (this.stopOptions && this.stopOptions.success) {
      this.stopOptions.success({
        SuggestedScore: suggestedScore,
        PronAccuracy: Number(result.PronAccuracy) || 0,
        PronFluency: Number(result.PronFluency) || 0,
        PronCompletion: Number(result.PronCompletion) || 0,
        blob: this.stopBlob,
        AudioUrl: '',
        Words: result.Words || [],
        rawResult: result.rawResult || result,
      });
    }

    this.stopOptions = null;
  }

  private resetResultState(): void {
    this.lastResult = null;
    this.stopOptions = null;
    this.stopBlob = null;
    this.resultResolved = false;
    this.clearFallbackTimer();
  }

  private clearFallbackTimer(): void {
    if (this.fallbackTimer !== null) {
      window.clearTimeout(this.fallbackTimer);
      this.fallbackTimer = null;
    }
  }

  private cleanupAudio(): void {
    if (this.processor) {
      this.processor.disconnect();
      this.processor.onaudioprocess = null;
      this.processor = null;
    }
    if (this.mediaStreamSource) {
      this.mediaStreamSource.disconnect();
      this.mediaStreamSource = null;
    }
    if (this.audioContext) {
      this.audioContext.close().catch(() => { });
      this.audioContext = null;
    }
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      this.mediaRecorder.stop();
    }
    this.mediaRecorder = null;
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach((track) => track.stop());
      this.mediaStream = null;
    }
  }
}

export default VoiceEvaluationService;
